import type React from 'react';

import { DeleteVariantIcon } from '@vlabs/icons';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { queryClient } from '@vlabs/api';
import { useGetDepartments } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './DeleteDepartmentsConfirmation.module.sass';
import { useDeleteDepartmentTranslation } from './i18n';

const displayName = 'DeleteDepartmentsConfirmation';
const testid = buildTestid(displayName);

interface DeleteDepartmentsConfirmationProps extends WithDataTestid {
  departmentIds: DepartmentId[];
  onConfirm: VoidFunction;
  onReject: VoidFunction;
}

function DeleteDepartmentsConfirmation({
  departmentIds,
  onConfirm,
  onReject,
  'data-testid': dataTestId = undefined,
}: DeleteDepartmentsConfirmationProps): React.JSX.Element {
  const { t } = useDeleteDepartmentTranslation();
  const count = departmentIds.length;
  const { data } = useGetDepartments({ department_ids: departmentIds }, undefined, queryClient);
  const departmentName = data?.departments?.[0]?.name ?? departmentIds[0];

  return (
    <div
      className={st.DeleteDepartmentsConfirmation}
      {...testid(dataTestId)}
    >
      <div className={st.Header}>
        <DeleteVariantIcon className={st.Icon} />
        <div className={st.Description}>
          <span className={st.Title}>{t('confirm_title', { count })}</span>
          <span className={st.Subtitle}>
            {t('confirm_description', {
              count,
              departmentName,
            })}
          </span>
        </div>
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Action}
          kind="secondary"
          onClick={onReject}
          variant="outlined"
        >
          {t('confirm_cancel')}
        </Button>
        <Button
          className={st.Action}
          kind="negative"
          onClick={onConfirm}
          variant="filled"
        >
          {t('confirm_submit')}
        </Button>
      </div>
    </div>
  );
}

DeleteDepartmentsConfirmation.displayName = displayName;

export { DeleteDepartmentsConfirmation };
